import React, { Component } from "react";
import { RouteComponentProps } from "react-router";
import ApiService from "../../_services/ApiService";
import OverviewTab from "../../components/StepAnalyticsModal/OverviewTab";
import PhaseAnalyticsTab from "../../components/StepAnalyticsModal/PhaseAnalyticsTab";
import StudentsTab from "../../components/StepAnalyticsModal/StudentsTab";
import TestsTab from "../../components/StepAnalyticsModal/TestsTab";
import "./step-analytics.css";

interface StepAnalyticsState {
  activeTab: string;
  loading: boolean;
  error: string | null;
  analyticsData: any | null;
  stepName: string;
}

export class StepAnalytics extends Component<RouteComponentProps, StepAnalyticsState> {
  state: StepAnalyticsState = {
    activeTab: "overview",
    loading: true,
    error: null,
    analyticsData: null,
    stepName: "",
  };

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    const params = new URLSearchParams(window.location.search);
    const lessonPlanId = params.get("lessonPlanId");
    const stepId = params.get("stepId");
    const batchId = params.get("batchId");
    const stepName = params.get("stepName") || "Step Analytics";

    this.setState({ stepName });

    if (!lessonPlanId || !stepId || !batchId) {
      this.setState({
        error: "Missing required parameters",
        loading: false
      });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response: any = await ApiService.getLessonPlanStepActivity({
        lessonPlanId: Number(lessonPlanId),
        stepId: Number(stepId),
        batchId: Number(batchId),
      });

      if (response.data && response.data.success) {
        this.setState({
          analyticsData: response.data.data,
          loading: false,
        });
      } else {
        this.setState({
          error: "Failed to load analytics data",
          loading: false,
        });
      }
    } catch (error: any) {
      console.error("Error loading analytics:", error);
      this.setState({
        error: error.response?.data?.message || "Failed to load analytics",
        loading: false,
      });
    }
  };

  switchTab = (tabName: string) => {
    this.setState({ activeTab: tabName });
  };

  goBack = () => {
    window.close();
  };

  render() {
    const { activeTab, loading, error, analyticsData, stepName } = this.state;

    return (
      <div className="step-analytics-page">
        {/* Header */}
        <div className="analytics-page-header">
          <div className="header-content">
            <button className="btn-back" onClick={this.goBack}>
              ← Back
            </button>
            <h1 className="page-title">
              📊 Student Activity Analytics - {stepName}
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="analytics-page-body">
          {loading && (
            <div className="analytics-loading-page">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p>Loading analytics data...</p>
            </div>
          )}

          {error && (
            <div className="analytics-error-page">
              <div className="alert alert-danger" role="alert">
                <strong>Error:</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ml-3"
                  onClick={this.loadAnalytics}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && analyticsData && (
            <>
              {/* Tab Navigation */}
              <div className="analytics-page-tabs">
                <button
                  className={`analytics-page-tab ${activeTab === "overview" ? "active" : ""}`}
                  onClick={() => this.switchTab("overview")}
                >
                  📋 Overview
                </button>
                <button
                  className={`analytics-page-tab ${activeTab === "phases" ? "active" : ""}`}
                  onClick={() => this.switchTab("phases")}
                >
                  📚 Phase Analytics
                </button>
                <button
                  className={`analytics-page-tab ${activeTab === "students" ? "active" : ""}`}
                  onClick={() => this.switchTab("students")}
                >
                  👥 Students
                </button>
                <button
                  className={`analytics-page-tab ${activeTab === "tests" ? "active" : ""}`}
                  onClick={() => this.switchTab("tests")}
                >
                  ✅ Tests
                </button>
              </div>

              {/* Tab Content */}
              <div className="analytics-page-content">
                {activeTab === "overview" && (
                  <OverviewTab
                    data={analyticsData}
                    step={{ name: stepName }}
                  />
                )}
                {activeTab === "phases" && (
                  <PhaseAnalyticsTab data={analyticsData} />
                )}
                {activeTab === "students" && (
                  <StudentsTab data={analyticsData} />
                )}
                {activeTab === "tests" && (
                  <TestsTab data={analyticsData} />
                )}
              </div>
            </>
          )}

          {!loading && !error && !analyticsData && (
            <div className="alert alert-info">
              No analytics data available yet.
            </div>
          )}
        </div>
      </div>
    );
  }
}
